import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { SUPPORTED_LANGUAGES } from '../i18n';
import '../styles/login.css';

/* ── Particle system types ── */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  color: { r: number; g: number; b: number };
  alpha: number;
  pulseSpeed: number;
  pulsePhase: number;
}

const PARTICLE_COUNT = 60;
const CONNECTION_DIST = 160;
const COLORS = [
  { r: 46, g: 229, b: 157 },
  { r: 122, g: 167, b: 255 },
  { r: 255, g: 209, b: 102 },
  { r: 234, g: 240, b: 255 },
];

export default function Login() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorVisible, setErrorVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [clock, setClock] = useState('--:--');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [fadeOut, setFadeOut] = useState(false);
  const [, setPostLoginTarget] = useState<string>('/dashboard');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  /* ── Lock body scroll on login page ── */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.style.background = '#070b14';
    return () => {
      document.body.style.overflow = prev;
      document.body.style.background = '';
    };
  }, []);

  /* ── Clock ── */
  useEffect(() => {
    const update = () => {
      const now = new Date();
      setClock(String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0'));
    };
    update();
    const id = setInterval(update, 5000);
    return () => clearInterval(id);
  }, []);

  /* ── Particle canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let W = (canvas.width = window.innerWidth);
    let H = (canvas.height = window.innerHeight);

    const particles: Particle[] = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const c = COLORS[Math.floor(Math.random() * COLORS.length)];
      particles.push({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 2 + 0.5,
        color: c,
        alpha: Math.random() * 0.4 + 0.15,
        pulseSpeed: Math.random() * 0.02 + 0.005,
        pulsePhase: Math.random() * Math.PI * 2,
      });
    }

    const handleResize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    const draw = (time: number) => {
      animRef.current = requestAnimationFrame(draw);
      ctx.clearRect(0, 0, W, H);

      // Connection lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const alpha = (1 - dist / CONNECTION_DIST) * 0.1;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(122,167,255,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Particles
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = W + 10;
        if (p.x > W + 10) p.x = -10;
        if (p.y < -10) p.y = H + 10;
        if (p.y > H + 10) p.y = -10;
        const pulse = Math.sin(time * p.pulseSpeed + p.pulsePhase) * 0.3 + 0.7;
        const a = p.alpha * pulse;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color.r},${p.color.g},${p.color.b},${a})`;
        ctx.fill();
        if (p.r > 1.5) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${p.color.r},${p.color.g},${p.color.b},${a * 0.12})`;
          ctx.fill();
        }
      }
    };
    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /* ── Error display ── */
  const showError = useCallback((msg: string) => {
    setError(msg);
    setErrorVisible(true);
    setShaking(true);
    setTimeout(() => setShaking(false), 500);
    setTimeout(() => setErrorVisible(false), 4000);
  }, []);

  /* ── Form submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorVisible(false);
    if (!email || !password) {
      showError(t('login.enterBothFields'));
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      const savedUser = JSON.parse(localStorage.getItem('sp_user') || '{}');
      // Force password change for invited users
      if (savedUser.must_change_password) {
        navigate('/change-password', { replace: true });
        return;
      }
      // Platform staff (owners + engineers) → dashboard, others → site selector
      const target =
        savedUser.is_platform && (savedUser.role === 'OWNER' || savedUser.role === 'ENGINEER')
          ? '/dashboard'
          : '/select-site';
      setPostLoginTarget(target);
      setFadeOut(true);
      setTimeout(() => navigate(target, { replace: true }), 1100);
      return;
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.startsWith('Temporary password expired')) {
        showError(t('login.tempPasswordExpired'));
      } else {
        showError(detail || t('login.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative' }}>
      {/* Fade-to-dark overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          background: '#070b14',
          opacity: fadeOut ? 1 : 0,
          transition: 'opacity 1s ease-in',
          pointerEvents: 'none',
        }}
      />
      {/* Background layers */}
      <div className="lp-aurora" />
      <div className="lp-aurora-drift" />
      <div className="lp-hex-grid" />
      <canvas ref={canvasRef} className="lp-particles" />
      <div className="lp-scan-line" />

      {/* Floating orbs */}
      <div className="lp-orb lp-orb-1" />
      <div className="lp-orb lp-orb-2" />
      <div className="lp-orb lp-orb-3" />

      {/* Neural pulse lines */}
      <div className="lp-pulse-line lp-pulse-line-1" />
      <div className="lp-pulse-line lp-pulse-line-2" />
      <div className="lp-pulse-line lp-pulse-line-3" />

      {/* Corner decorations */}
      <div className="lp-corner lp-corner-tl" />
      <div className="lp-corner lp-corner-tr" />
      <div className="lp-corner lp-corner-bl" />
      <div className="lp-corner lp-corner-br" />

      {/* Center content — wrapped in <main> for axe landmark rules */}
      <main className="lp-center-stage" aria-labelledby="login-heading">
        <div className="lp-logo-container">
          <div className="lp-logo-glow" />
          <div className="lp-orbital-ring" />
          <div className="lp-orbital-ring-2" />
          <img src="/logoforhtml.png" alt="SmartPitch" className="lp-logo-img" />
        </div>

        {import.meta.env.VITE_ENV_LABEL && <div className="lp-env-label">{import.meta.env.VITE_ENV_LABEL}</div>}

        <div className={`lp-login-card${shaking ? ' lp-shake' : ''}`}>
          <h1 id="login-heading" className="lp-login-title">
            {t('login.title')}
          </h1>
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="lp-input-group">
              <label htmlFor="login-username" className="lp-input-label">
                {t('login.username')}
              </label>
              <input
                id="login-username"
                className="lp-input-field"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
              />
            </div>
            <div className="lp-input-group">
              <label htmlFor="login-password" className="lp-input-label">
                {t('login.password')}
              </label>
              <input
                id="login-password"
                className="lp-input-field"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button className="lp-login-btn" type="submit" disabled={loading} aria-label={t('login.signIn')}>
              {loading ? (
                <>
                  <span className="lp-spinner" />
                  {t('login.authenticating')}
                </>
              ) : (
                t('login.signIn')
              )}
            </button>
          </form>
          <div className={`lp-error-msg${errorVisible ? ' visible' : ''}`}>{error}</div>
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <Link to="/forgot-password" className="lp-toggle-btn">
              {t('login.forgotPassword')}
            </Link>
          </div>
          <div style={{ textAlign: 'center', marginTop: 18, fontSize: 11, color: 'rgba(122,167,255,0.5)' }}>
            <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none', marginRight: 12 }}>
              {t('legal.privacy')}
            </Link>
            <span style={{ opacity: 0.4 }}>·</span>
            <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none', marginLeft: 12 }}>
              {t('legal.terms')}
            </Link>
          </div>
        </div>
      </main>

      {/* Status bar */}
      <div className="lp-status-bar">
        <span className="lp-status-dot" />
        <span>SmartPitch</span>
        <span className="lp-status-separator" />
        <span>{clock}</span>
        <span className="lp-status-separator" />
        <select
          aria-label={t('common.language')}
          value={i18n.language}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(122,167,255,0.6)',
            fontSize: 10,
            fontFamily: 'inherit',
            cursor: 'pointer',
            outline: 'none',
            padding: 0,
          }}
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code} style={{ background: '#0d1525', color: '#8892b0' }}>
              {l.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
